import React from 'react';
import { styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { authColors, authFontFamily, authLayout, authType } from 'app/pages/auth/shared/authTheme';

/**
 * Shared two-panel shell for every redesigned auth screen (SEE-213): a left
 * form column and a right branded splash panel. The splash is a single exported
 * asset (identical on all screens) and collapses below `md`, where the form
 * takes the full width — mirroring the legacy auth layout's responsive rule.
 */

const Root = styled('div')({
  fontFamily: authFontFamily,
  display: 'flex',
  alignItems: 'stretch',
  width: '100%',
  // SEE-1715: #root is padded below the status bar in the mobile shell (0
  // elsewhere); take it off so the footer still sits on screen.
  minHeight: 'calc(100vh - var(--native-safe-top, 0px))',
  backgroundColor: authColors.white,
});

const FormPane = styled('div')(({ theme }) => ({
  position: 'relative',
  flex: '1 0 0',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  // Bottom padding reserves the absolute footer's zone (bottom: 40 + its
  // height) so page content can never overlap "Need help?" on short
  // viewports — e.g. the register page inside the mobile WebView.
  padding: '50px 24px 96px',
  [theme.breakpoints.up('sm')]: { padding: '50px 48px 96px' },
  [theme.breakpoints.up(1160)]: { padding: '50px 144px 96px' },
}));

const FormContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  alignItems: 'center',
  width: '100%',
  maxWidth: authLayout.formMaxWidth,
});

const SplashPane = styled('div')(({ theme }) => ({
  position: 'relative',
  // Proportional (not fixed px) so the ~58/42 split from Figma holds at any
  // width; flex-basis + shrink:0 keeps it from collapsing next to the form.
  flex: `0 0 ${authLayout.splashWidthPct}`,
  overflow: 'hidden',
  borderTopLeftRadius: authLayout.splashRadius,
  borderBottomLeftRadius: authLayout.splashRadius,
  [theme.breakpoints.down(authLayout.splashBreakpoint)]: { display: 'none' },
}));

const SplashImg = styled('img')({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center',
});

const Footer = styled('div')({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 40,
  display: 'flex',
  justifyContent: 'center',
});

const NeedHelpButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
  color: authColors.textMuted,
  fontFamily: authFontFamily,
  ...authType.helper,
  '& svg': { fontSize: 18 },
});

export interface AuthSplitLayoutProps {
  /** Optional centered screen title (e.g. "Login"). */
  title?: React.ReactNode;
  children: React.ReactNode;
  /** Show the bottom-centered "Need help?" affordance (default true). */
  showNeedHelp?: boolean;
  /** Defaults to opening a support email when not provided. */
  onNeedHelp?: () => void;
  /** Override the form column max width for wider forms (e.g. registration). */
  maxWidth?: number;
}

const openSupportEmail = () => {
  window.location.href = 'mailto:support@speedchain.com';
};

export default function AuthSplitLayout({
  title,
  children,
  showNeedHelp = true,
  onNeedHelp = openSupportEmail,
  maxWidth,
}: AuthSplitLayoutProps) {
  const { t } = useTranslation();

  return (
    <Root>
      <FormPane>
        <FormContent sx={maxWidth ? { maxWidth } : undefined}>
          {title != null && (
            <Typography component="h1" sx={{ ...authType.title, textAlign: 'center', width: '100%' }}>
              {title}
            </Typography>
          )}
          {children}
        </FormContent>
        {showNeedHelp && (
          <Footer>
            <NeedHelpButton type="button" onClick={onNeedHelp}>
              <InfoOutlinedIcon />
              {t('needHelp')}
            </NeedHelpButton>
          </Footer>
        )}
      </FormPane>
      <SplashPane aria-hidden>
        <SplashImg src="/assets/images/auth/login-splash.png" alt="" />
      </SplashPane>
    </Root>
  );
}
